import useLocalStorageState from "@/hooks/useLocalStorageState";
import { api } from "@/lib/api";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useAuth } from "./auth.context";
import useDomain from "@/hooks/use-domain";
import { app_env, env_config } from "@/env-config";

const OrganizationContext = createContext<any | null>(null);

export function OrganizationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();

  const { orgDomain, isAdmin } = useDomain(env_config[app_env].domain);

  const {
    data: domainOrganization,
    isLoading: isLoadingDomainOrg,
    error: domainOrgError,
  } = useQuery(
    ["current_organization_domain", orgDomain],
    async () => {
      const { data } = await api.get(`/organizations/domain/${orgDomain}`);
      return data;
    },
    {
      enabled: !!orgDomain,
      retry: false,
    },
  );

  /**
   * Why the domain lookup failed: a domain nobody owns vs. a workspace that
   * exists but was suspended. Layouts render a different screen for each.
   */
  const orgStatus = useMemo(() => {
    if (!orgDomain || isLoadingDomainOrg) return null;
    if (domainOrganization) return null;
    if (!domainOrgError) return null;

    const meta = (domainOrgError as any)?.response?.data?.meta;
    return {
      reason:
        meta?.reason === "ORGANIZATION_INACTIVE"
          ? "inactive"
          : meta?.reason === "ORGANIZATION_SUSPENDED"
            ? "suspended"
            : "not-found",
      organizationName: meta?.organizationName as string | undefined,
      lastActivityAt: meta?.lastActivityAt as string | undefined,
      domain: orgDomain,
    } as const;
  }, [orgDomain, isLoadingDomainOrg, domainOrganization, domainOrgError]);

  const {
    data: organizations,
    isLoading,
    refetch,
  } = useQuery(
    ["organizations", user?.id, "mine", domainOrganization],
    async () => {
      if (domainOrganization?.id) {
        const { data } = await api.get(
          `/organizations/mine/${domainOrganization?.id}`,
        );
        return [data];
      }
      const { data } = await api.get("/organizations/mine");
      return data;
    },
    {
      enabled: (!!user && !isAdmin) || Boolean(domainOrganization?.id),
      retry: false,
    },
  );

  const [activeOrg, setActiveOrg] = useLocalStorageState(`active_org`, null);

  const orgg = organizations?.find((org) => org.id === activeOrg);

  /**
   * AUTO SET ACTIVE ORG
   */
  useEffect(() => {
    if (!organizations) return;

    // if domain org exists, prioritize it
    if (domainOrganization?.id) {
      setActiveOrg(domainOrganization.id);
      return;
    }

    // if no active org yet
    if (!activeOrg && organizations.length > 0) {
      setActiveOrg(organizations[0].id);
      return;
    }

    // if activeOrg no longer exists in organizations
    const exists = organizations.some((org) => org.id === activeOrg);
    if (!exists && organizations.length > 0) {
      setActiveOrg(organizations[0].id);
    }
  }, [organizations, domainOrganization, activeOrg, setActiveOrg]);

  const contextValue = useMemo(
    () => ({
      organizations: organizations?.map((org) => ({
        ...org,
        role: org?.memberShip?.role?.name,
      })),
      refetch: () => {
        refetch();
      },
      isLoading: isLoading || isLoadingDomainOrg,
      setActiveOrg,
      orgStatus,
      activeOrg: orgg ? orgg : domainOrganization,
      memberShip: orgg?.memberShip,
      role: orgg?.memberShip?.role,
      /**
       * Subscription state, or null when this org is not billed. Comes down
       * with the organization payload so the renewal banner costs no extra
       * request. A *locked* org never gets this far — the API rejects with 402
       * and the interceptor routes to /billing-required.
       */
      billing: (orgg ?? domainOrganization)?.billing ?? null,
    }),
    [
      organizations,
      isLoading,
      activeOrg,
      orgg?.memberShip,
      isLoadingDomainOrg,
      orgg,
      orgStatus,
    ],
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
}
